import { useMemo } from "react";
import { Pencil } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../../../components/ui/sheet";
import { Button } from "../../../../components/ui/button";
import {
  FieldGroup,
  FieldGroupInstance,
  getInstanceDisplaySlug,
  WrappedFieldValue,
} from "../../../../models/audit/library";
import { useWorkspaceMembers } from "../../../../hooks/useWorkspaceMembers";

interface LibraryItemDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance: FieldGroupInstance | null;
  fieldGroup: FieldGroup | null;
  onEdit: (instance: FieldGroupInstance) => void;
  canEdit: boolean;
}

// Helper to format datetime string for display
function formatDateForDisplay(datetime: string, includeTime?: boolean): string {
  const date = new Date(datetime);
  if (includeTime) {
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Helper to unwrap and display field value (returns raw value, user lookup handled separately)
function getDisplayValue(
  wrappedValue: WrappedFieldValue,
  includeTime?: boolean,
): string | string[] {
  if ("text" in wrappedValue) return wrappedValue.text;
  if ("userIds" in wrappedValue) {
    // Return the array of user IDs - email lookup handled in component
    return wrappedValue.userIds;
  }
  if ("datetime" in wrappedValue)
    return formatDateForDisplay(wrappedValue.datetime, includeTime);
  return "";
}

export function LibraryItemDrawer({
  open,
  onOpenChange,
  instance,
  fieldGroup,
  onEdit,
  canEdit,
}: LibraryItemDrawerProps) {
  const { users } = useWorkspaceMembers({ fetchOnLoad: true });

  // Create a lookup map from user ID to email
  const userEmailMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of users) {
      map.set(user.id, user.email);
    }
    return map;
  }, [users]);

  if (!instance) return null;

  const displaySlug = getInstanceDisplaySlug(instance, fieldGroup);
  const fieldDefinitions = (fieldGroup?.fieldDefinitions ?? []).sort(
    (a, b) => a.displayOrder - b.displayOrder,
  );

  const handleEditClick = () => {
    onEdit(instance);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-xl font-semibold">
            {displaySlug}
          </SheetTitle>
          <SheetDescription>{displaySlug}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {canEdit && (
            <Button
              variant="outline"
              className="w-full"
              onClick={handleEditClick}
            >
              <Pencil className="h-4 w-4 mr-2" />
              Edit {fieldGroup?.name ?? "Item"}
            </Button>
          )}

          {instance.description && (
            <div>
              <h3 className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-2">
                Description
              </h3>
              <p className="text-sm text-stone-900">{instance.description}</p>
            </div>
          )}

          <div>
            <h3 className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-3">
              Details
            </h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-stone-500">
                  {fieldGroup?.name ?? "Item"} Name
                </dt>
                <dd className="text-sm text-stone-900 text-right">
                  {instance.name}
                </dd>
              </div>

              {fieldDefinitions.map((fieldDef) => {
                const valueEntry = instance.values?.find(
                  (v) => v.fieldDefinition.id === fieldDef.id,
                );
                let displayValue = "—";
                if (valueEntry) {
                  const fieldConfig = (fieldDef.fieldConfig ?? {}) as Record<
                    string,
                    unknown
                  >;
                  const includeTime = fieldConfig.include_time === true;
                  const rawValue = getDisplayValue(
                    valueEntry.value,
                    includeTime,
                  );
                  // For user fields, look up emails from user IDs
                  if (
                    "userIds" in valueEntry.value &&
                    Array.isArray(rawValue)
                  ) {
                    const emails = rawValue
                      .map((userId) => userEmailMap.get(userId) ?? userId)
                      .filter(Boolean);
                    displayValue = emails.length > 0 ? emails.join(", ") : "—";
                  } else if (typeof rawValue === "string") {
                    displayValue = rawValue || "—";
                  }
                }

                return (
                  <div key={fieldDef.id} className="flex justify-between">
                    <dt className="text-sm text-stone-500">{fieldDef.name}</dt>
                    <dd className="text-sm text-stone-900 text-right">
                      {displayValue}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
